import { useEffect, useState } from 'react';
import { Formik, Form } from 'formik';
import { isEqual } from 'lodash';

import Yup from '../../utils/yup';
import itemTypePropertyService from '../../service/itemTypeProperty.service';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import useMenus from '../../context/MenuContext';
import { DeleteDialogUI, FormBase, FormButtonPanelUI, FormHeaderUI } from '../Interface';
import { cleanValues } from '../../utils/helper';
import ItemTypePropertyFields from './ItemTypePropertyFields';
import ItemTypePropertyReports from './ItemTypePropertyReports';
import ItemTypePropertyValuesGrid from './ItemTypePropertyValuesGrid';

const defaultFormValues = {
  id: null,
  name: '',
  dataType: '',
  active: true,
  mandatory: false,
  externalCode: '',
};

const normalizeComboRows = (rows = []) => {
  return rows.map((row) => ({
    id: row.id,
    value: row.value ?? '',
    externalCode: row.externalCode ?? '',
  }));
};

const ItemTypePropertyPage = () => {
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const [refetchTable, setRefetchTable] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [editButtonVisible, setEditButtonVisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [canEdit, setCanEdit] = useState(true);
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [selectedValues, setSelectedValues] = useState({});
  const [initValues, setInitValues] = useState(defaultFormValues);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [comboValueRows, setComboValueRows] = useState([]);
  const [originalComboValueRows, setOriginalComboValueRows] = useState([]);
  const [comboSubmitAttempted, setComboSubmitAttempted] = useState(false);

  useEffect(() => {
    getMenus();
  }, []);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('itemTypeProperty');
    setCanEdit(getCanEdit(submenuId));
  }, [menus, getCanEdit, getMenuSubMenuId]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      const selectedComboRows = normalizeComboRows(
        selectedValues?.itemTypePropertyValues ?? selectedValues?.values ?? selectedValues?.itemTypePropertyValue ?? []
      );

      if (!isEqual(defaultFormValues, selectedValues)) {
        setInitValues({
          id: selectedValues.id ?? null,
          name: selectedValues.name ?? '',
          dataType: selectedValues.dataType ?? '',
          active: selectedValues.active ?? true,
          mandatory: selectedValues.mandatory ?? false,
          externalCode: selectedValues.externalCode ?? '',
        });
        setComboValueRows(selectedComboRows);
        setOriginalComboValueRows(selectedComboRows);
        setComboSubmitAttempted(false);
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
        setInitValues(defaultFormValues);
        setComboValueRows([]);
        setOriginalComboValueRows([]);
        setComboSubmitAttempted(false);
      }
    } else {
      setOperatorsDisabled(true);
      setInitValues(defaultFormValues);
      setComboValueRows([]);
      setOriginalComboValueRows([]);
      setComboSubmitAttempted(false);
    }
  }, [selectedValues]);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(),
    dataType: Yup.string().required(),
    active: Yup.boolean(),
    mandatory: Yup.boolean(),
    externalCode: Yup.string().nullable(),
  });

  const refreshSelection = async (data) => {
    const selectedId = data?.id ?? initValues.id;
    if (!selectedId) return;
    const detail = await itemTypePropertyService.getItem(selectedId);
    setSelectedValues(detail);
  };

  const hasInvalidComboRows = () => {
    return comboValueRows.some((row) => !row.value?.trim());
  };

  const syncComboValues = async (itemTypePropertyId) => {
    const originalById = new Map(
      originalComboValueRows.filter((row) => row.id && !String(row.id).startsWith('new_')).map((row) => [row.id, row])
    );
    const currentExistingRows = comboValueRows.filter((row) => row.id && !String(row.id).startsWith('new_'));
    const currentExistingIdSet = new Set(currentExistingRows.map((row) => row.id));

    const createRows = comboValueRows.filter((row) => String(row.id).startsWith('new_'));
    const updateRows = currentExistingRows.filter((row) => {
      const originalRow = originalById.get(row.id);
      if (!originalRow) return false;
      return originalRow.value !== row.value || (originalRow.externalCode ?? '') !== (row.externalCode ?? '');
    });
    const deleteRows = originalComboValueRows.filter(
      (row) => row.id && !String(row.id).startsWith('new_') && !currentExistingIdSet.has(row.id)
    );

    await Promise.all(
      createRows.map((row) =>
        itemTypePropertyService.createValue(itemTypePropertyId, {
          value: row.value.trim(),
          externalCode: row.externalCode?.trim() || null,
        })
      )
    );

    await Promise.all(
      updateRows.map((row) =>
        itemTypePropertyService.updateValue({
          id: row.id,
          value: row.value.trim(),
          externalCode: row.externalCode?.trim() || null,
        })
      )
    );

    await Promise.all(deleteRows.map((row) => itemTypePropertyService.deleteValue(row.id)));
    setComboSubmitAttempted(false);
    return true;
  };

  const handleSubmit = async (values) => {
    if (values.dataType === 'COMBO') {
      setComboSubmitAttempted(true);
      if (hasInvalidComboRows()) return;
    }

    if (!editButtonVisible) {
      const data = await itemTypePropertyService.updateItem(cleanValues(values));
      if (values.dataType === 'COMBO') {
        await syncComboValues(data.id ?? values.id);
      }
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });
      await refreshSelection(data);
      setRefetchTable(true);
      setEditButtonVisible(true);
      setViewField(true);
    } else if (!addField) {
      const data = await itemTypePropertyService.createItem(cleanValues(values));
      if (values.dataType === 'COMBO') {
        await syncComboValues(data.id);
      }
      addNotification({
        content: 'Sikeres mentés!',
        type: NotificationType.SUCCESS,
      });
      await refreshSelection(data);
      setRowSelectionModel(data?.id ? [data.id] : []);
      setRefetchTable(true);
      setAddField(true);
      setViewField(true);
    }
  };

  const handleDelete = async () => {
    await itemTypePropertyService.deleteItem(selectedValues.id);
    addNotification({
      content: 'Sikeres törlés!',
      type: NotificationType.WARNING,
    });
    setShowDeleteDialog(false);
    setSelectedValues(defaultFormValues);
    setRefetchTable(true);
    setViewField(true);
    setEditButtonVisible(true);
    setAddField(true);
  };

  return (
    <>
      <FormBase
        name="ItemTypeProperty"
        leftPanelContent={
          <div className="h-full overflow-auto custom-scrollbar">
            <FormHeaderUI
              addClick={() => {
                setSelectedValues(defaultFormValues);
                setViewField(false);
                setAddField(false);
              }}
              addDisabled={!viewField || !canEdit}
              modClick={() => {
                setViewField(false);
                setEditButtonVisible(false);
              }}
              modDisabled={!viewField || operatorsDisabled || !canEdit}
              copyClick={() => {
                setSelectedValues({
                  ...initValues,
                  id: null,
                });
                setViewField(false);
                setAddField(false);
              }}
              copyDisabled={!viewField || operatorsDisabled || !canEdit}
              deleteClick={() => {
                setShowDeleteDialog(true);
              }}
              deleteDisabled={!viewField || operatorsDisabled || !canEdit}
            />

            <Formik
              initialValues={initValues}
              validationSchema={validationSchema}
              validateOnChange={false}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({ resetForm, values }) => (
                <Form>
                  <ItemTypePropertyFields viewField={viewField} />

                  {values.dataType === 'COMBO' && (
                    <ItemTypePropertyValuesGrid
                      disabled={viewField}
                      rows={comboValueRows}
                      setRows={setComboValueRows}
                      submitAttempted={comboSubmitAttempted}
                    />
                  )}

                  <FormButtonPanelUI
                    onCancelClick={() => {
                      setViewField(true);
                      setEditButtonVisible(true);
                      setAddField(true);
                      setComboSubmitAttempted(false);
                      resetForm();
                    }}
                    disabled={editButtonVisible && addField}
                  />
                </Form>
              )}
            </Formik>
          </div>
        }
        rightPanelContent={
          <ItemTypePropertyReports
            refetch={{ refetchTable, setRefetchTable }}
            values={{ selectedValues, setSelectedValues }}
            viewField={viewField}
            operatorsDisabled={operatorsDisabled}
            rowSelection={{ rowSelectionModel, setRowSelectionModel }}
          />
        }
      />
      <DeleteDialogUI
        handleRemoveElement={handleDelete}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </>
  );
};

export default ItemTypePropertyPage;
